import { useContext, useEffect, useRef, useState } from 'react';
import AuthContext from '../Context/AuthContext';
import Popup from '../Components/Popup';
import ButtonSolid from '../Components/ButtonSolid';
import ButtonOutline from '../Components/ButtonOutline';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { baseURL } from '../config';
import toast from 'react-hot-toast';
import LoadingAnimation from '../Components/LoadingAnimation';
import PaginationComponent from '../Components/PaginationComponent';
import MultiSelectDropdown from '../Components/MultiSelectDropdown';
import Searchbar from '../Components/Searchbar';
import ProductLibraryContext from '../Context/ProductLibraryContext';
import { EditIcon, EyeIcon, DeleteIcon, CloseIcon, CheckCircle } from '../Components/Icons';

// One line per kind of record that still uses the assembly, e.g.
// "2 assemblies: 109-1000-C, 600 and 1 more".
const describeUsage = (usage) => [
  ['assembly', 'assemblies', usage.assemblies],
  ['MODs template', 'MODs templates', usage.templates],
  ['machine order', 'machine orders', usage.machine_orders],
]
  .filter(([, , group]) => group.count > 0)
  .map(([one, many, group]) => {
    const more = group.count > group.items.length ? ` and ${group.count - group.items.length} more` : '';
    return `${group.count} ${group.count === 1 ? one : many}: ${group.items.join(', ')}${more}`;
  });

function EditableCell({ value, className, field, isEditing, editValue, onEditValueChange, onStartEdit, onSave, onCancel, saving, assemblyTypeOptions }) {
  if (isEditing) {
    return (
      <td className={`px-4 py-2 ml:py-6 border-r border-gray-300 ${className || ''}`} onClick={(e) => e.stopPropagation()}>
        <div className='flex items-center gap-2'>
          {field === 'assembly_type' ? (
            <>
              <input
                autoFocus
                list='inline-assembly-type-options'
                value={editValue}
                disabled={saving}
                onChange={(e) => onEditValueChange(e.target.value)}
                className='block w-full h-9 rounded-md border border-gray-300 focus:outline-gray-400 px-2 text-sm disabled:bg-gray-100'
              />
              <datalist id='inline-assembly-type-options'>
                {(assemblyTypeOptions || []).map((type) => <option key={type} value={type} />)}
              </datalist>
            </>
          ) : (
            <input
              autoFocus
              value={editValue}
              disabled={saving}
              onChange={(e) => onEditValueChange(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') onSave(); if (e.key === 'Escape') onCancel(); }}
              className='block w-full h-9 rounded-md border border-gray-300 focus:outline-gray-400 px-2 text-sm disabled:bg-gray-100'
            />
          )}
          <div className='flex flex-col gap-1 shrink-0'>
            <button type='button' title='Save' disabled={saving} onClick={onSave} className='p-1 rounded-md hover:bg-green-100 text-green-600 disabled:opacity-50'>
              <CheckCircle />
            </button>
            <button type='button' title='Cancel' disabled={saving} onClick={onCancel} className='p-1 rounded-md hover:bg-red-100 text-red-500 disabled:opacity-50'>
              <CloseIcon size='w-5 h-5' />
            </button>
          </div>
        </div>
      </td>
    );
  }

  return (
    <td className={`px-4 py-2 ml:py-6 border-r border-gray-300 ${className || ''}`}>
      <div>
        <span>{value}</span>
        <button
          type='button'
          title='Edit'
          onClick={(e) => { e.stopPropagation(); onStartEdit(); }}
          className='inline-flex align-middle ml-2 p-1 rounded-md bg-white border border-gray-200 hover:bg-orange-400 hover:border-orange-400 text-gray-500 hover:text-white transition-colors'
        >
          <EditIcon size='w-3 h-3' />
        </button>
      </div>
    </td>
  );
}

function ProductLibraryListView() {
  const navigate = useNavigate()
  const [assemblyData, setAssemblyData] = useState([]);
  const [fetchingAssemblyData, setFetchingAssemblyData] = useState(true);
  const [fetchingAssemblyType, setFetchingAssemblyType] = useState(true);
  const { token, logout } = useContext(AuthContext);
  const { params, setParams, searchInput, setSearchInput } = useContext(ProductLibraryContext);
  const [totalPages, setTotalPages] = useState(1);
  const [totalAssemblies, setTotalAssemblies] = useState();
  const [assemblyType, setAssemblyType] = useState();
  const [editingCell, setEditingCell] = useState(null); // { id, field }
  const [editValue, setEditValue] = useState('');
  const [savingCell, setSavingCell] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // the assembly row being deleted
  const [deleteUsage, setDeleteUsage] = useState({ loading: false, data: null });
  const [deleting, setDeleting] = useState(false);
  const [reloadFlag, setReloadFlag] = useState(0);

  const startCellEdit = (id, field, currentValue) => {
    setEditingCell({ id, field });
    setEditValue(currentValue || '');
  };

  const cancelCellEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const saveCellEdit = async () => {
    const { id, field } = editingCell;
    try {
      setSavingCell(true);
      await axios.put(`${baseURL}/assembly/modifyAssembly/${id}`, { [field]: editValue }, {
        headers: { "ngrok-skip-browser-warning": "any", "x-access-token": token }
      });
      setAssemblyData((prev) => (Array.isArray(prev) ? prev.map((a) => a._id === id ? { ...a, [field]: editValue } : a) : prev));
      toast.success('Updated successfully');
      setEditingCell(null);
      setEditValue('');
    } catch (error) {
      if (error.response?.status === 401) {
        logout();
      } else {
        toast.error('Something went wrong, not updated');
      }
    } finally {
      setSavingCell(false);
    }
  };

  // An assembly that other records still use can't be deleted - the popup first
  // checks that and lists where it is used.
  const openDelete = async (assembly) => {
    setDeleteTarget(assembly);
    setDeleteUsage({ loading: true, data: null });
    try {
      const response = await axios.get(`${baseURL}/assembly/usage/${assembly._id}`, {
        headers: { "ngrok-skip-browser-warning": "any", "x-access-token": token }
      });
      setDeleteUsage({ loading: false, data: response.data.data });
    } catch (error) {
      setDeleteTarget(null);
      if (error.response?.status === 401) {
        logout();
      } else {
        toast.error('Could not check where this assembly is used');
      }
    }
  };

  const closeDelete = () => {
    if (deleting) return;
    setDeleteTarget(null);
    setDeleteUsage({ loading: false, data: null });
  };

  const confirmDelete = async () => {
    try {
      setDeleting(true);
      await axios.delete(`${baseURL}/assembly/deleteAssembly/${deleteTarget._id}`, {
        headers: { "ngrok-skip-browser-warning": "any", "x-access-token": token }
      });
      toast.success('Assembly deleted successfully');
      setDeleteTarget(null);
      setDeleteUsage({ loading: false, data: null });
      // Deleting the only row of a later page steps back a page; otherwise reload this one.
      if (Array.isArray(assemblyData) && assemblyData.length === 1 && params.page > 1) {
        handleCurrentPage(params.page - 1);
      } else {
        setReloadFlag((prev) => prev + 1);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        logout();
      } else if (error.response?.status === 409) {
        // Something started using it after the check - show where.
        setDeleteUsage({ loading: false, data: error.response.data.data });
      } else {
        toast.error('Something went wrong, assembly not deleted');
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleSetAssemblyFilter = (value) => {
    setParams(prevParams => ({
      ...prevParams,
      filter: value,
      page: 1
    }));
  }

  const useDebounce = (func, delay) => {
    const debounceRef = useRef(null);

    const debouncedFunc = (...args) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = window.setTimeout(() => {
        func(...args);
      }, delay);
    };

    debouncedFunc.cancel = () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };

    return debouncedFunc;
  };

  const handleSetSearchFilter = useDebounce((value) => {
    setParams(prevParams => ({
      ...prevParams,
      search: value,
      page: 1
    }))
  }, 800);

  const handleSearchFilter = (input) => {
    setSearchInput(input)
    handleSetSearchFilter(input)
  }

  const handleCurrentPage = (value) => {
    setParams(prevParams => ({
      ...prevParams,
      page: value
    }));
  }

  const handleLimitValue = (value) => {
    setParams(prevParams => ({
      ...prevParams,
      limit: value
    }));
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        setFetchingAssemblyData(true)
        const response = await axios.get(`${baseURL}/assembly/getAllAssemblies`,
          {
            headers: {
              "ngrok-skip-browser-warning": "any",
              "x-access-token": token
            },
            params: params
          });
        if (response?.status === 204) {
          setAssemblyData('no data');
          setTotalPages(1)
        } else {
          setAssemblyData(response.data.data.assemblyData)
          setTotalPages(response.data.data.pagination.totalPages)
          setTotalAssemblies(response.data.data.pagination.totalAssemblies)
        }
      } catch (error) {
        if (error.response?.status === 401) {
          logout();
        } else {
          console.error('Error:', error);
        }
      } finally {
        setFetchingAssemblyData(false)
      }
    };

    fetchData();
  }, [params, reloadFlag, logout, token]);

  useEffect(() => {
    const fetchAssemblyType = async () => {
      try {
        setFetchingAssemblyType(true)
        const response = await axios.get(`${baseURL}/template/getAssemblyTypes`,
          {
            headers: {
              "ngrok-skip-browser-warning": "any",
              "x-access-token": token
            }
          })
        setAssemblyType(response.data.data)
      } catch (error) {
        if (error.response?.status === 401) {
          logout();
        } else {
          console.error('Error:', error);
        }
      } finally {
        setFetchingAssemblyType(false)
      }
    };

    fetchAssemblyType()
  }, [logout, token]);

  const assembly_detail = (param) => {
    navigate(`/assembly_detail/${param}`)
  }

  return (
    <>
      <div className='pb-6'>
        <div className='flex flex-wrap md:flex-nowrap justify-between items-center gap-4 py-5'>
          <p className='text-lg font-medium md:text-xl md:font-semibold pr-3'>Assembly List</p>
        </div>
        <div className="p-3 bg-white shadow-md rounded-lg min-h-80">
          <div className='flex justify-between flex-wrap px-1 md:pt-3 md:pb-6 items-center gap-3'>
            <div className='pr-2 whitespace-nowrap text-xl font-medium'>Assembly Information</div>
            <div className="flex flex-wrap gap-4 pb-4 md:pb-0 w-full md:w-auto">
              <Searchbar
                value={searchInput}
                onChange={(e) => { handleSearchFilter(e.target.value) }}
                className={fetchingAssemblyData ? 'bg-gray-100' : ''}
              />
              <MultiSelectDropdown
                options={assemblyType}
                disabled={fetchingAssemblyData || fetchingAssemblyType}
                setValue={handleSetAssemblyFilter}
                placeholder={"Add Assembly Type Filter"}
              />
              <ButtonSolid
                disabled={fetchingAssemblyData}
                buttonText='Add Assembly'
                onClick={() => { navigate('/assembly/add') }}
              />
            </div>
          </div>
          {deleteTarget && (
            <Popup maxWidth='md:max-w-[500px]'>
              <div className='p-2'>
                <div className='flex justify-between items-center mb-4'>
                  <p className='text-lg font-semibold text-gray-700'>Delete Assembly</p>
                  <button
                    type='button'
                    disabled={deleting}
                    onClick={closeDelete}
                    className='p-2 border-2 bg-white border-gray-300 rounded-md cursor-pointer disabled:cursor-not-allowed'
                  >
                    <CloseIcon size='w-5 h-5' />
                  </button>
                </div>
                {deleteUsage.loading || !deleteUsage.data ? (
                  <LoadingAnimation />
                ) : deleteUsage.data.total > 0 ? (
                  <>
                    <p className='text-sm border-dashed border-2 border-red-500 bg-red-100 p-2 rounded-md mb-4'>
                      <span className='font-medium'>{deleteTarget.assembly_number}</span> can't be deleted because it is still used by:
                    </p>
                    <ul className='text-sm text-gray-700 list-disc pl-5 space-y-1 mb-4 break-words'>
                      {describeUsage(deleteUsage.data).map((line) => <li key={line}>{line}</li>)}
                    </ul>
                    <p className='text-sm text-gray-500 mb-5'>Remove it from these first, then delete it.</p>
                    <ButtonOutline buttonText='Close' onClick={closeDelete} className='w-full' />
                  </>
                ) : (
                  <>
                    <p className='text-sm text-gray-600 mb-2'>
                      Delete assembly <span className='font-medium'>{deleteTarget.assembly_number}</span>?
                    </p>
                    <p className='text-sm text-gray-500 mb-5'>
                      This permanently removes the assembly. Its assets are not deleted and stay available in the Asset Manager.
                    </p>
                    <div className='flex flex-col md:flex-row-reverse gap-4'>
                      <button
                        type='button'
                        disabled={deleting}
                        onClick={confirmDelete}
                        className={`w-full min-w-[150px] h-10 md:h-12 px-2 md:px-4 rounded-md text-sm md:text-base font-medium text-white bg-red-500 hover:bg-red-600 ${deleting ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {deleting ? 'Deleting...' : 'Delete'}
                      </button>
                      <ButtonOutline buttonText='Cancel' disabled={deleting} onClick={closeDelete} className='w-full' />
                    </div>
                  </>
                )}
              </div>
            </Popup>
          )}
          {fetchingAssemblyData ?
            (<LoadingAnimation />) :
            <div className='overflow-x-auto'>
              <table className="w-full text-sm text-left rtl:text-right text-gray-500 border border-gray-300">
                <thead className="text-gray-500 uppercase text-md bg-slate-50 border-b border-gray-300">
                  <tr>
                    <th className="px-4 py-3 min-w-[125px] border-r border-gray-300">Assembly #</th>
                    <th className="px-4 py-3 min-w-[240px] border-r border-gray-300">Assembly Type</th>
                    <th className="px-4 py-3 border-r border-gray-300">Assembly Description</th>
                    <th className="px-4 py-3 min-w-[150px] text-center">Actions</th>
                  </tr>
                </thead>
                {(assemblyData === 'no data' || assemblyData.length === 0) ?
                  <tbody>
                    <tr><td colSpan={4} className='p-4'>{`No Data found for '${params.search}'`}</td></tr>
                  </tbody> :
                  <tbody>
                    {assemblyData.map((data, index) => {
                      const cellProps = (field) => ({
                        field,
                        value: data?.[field],
                        isEditing: editingCell?.id === data?._id && editingCell?.field === field,
                        editValue,
                        onEditValueChange: setEditValue,
                        onStartEdit: () => startCellEdit(data?._id, field, data?.[field]),
                        onSave: saveCellEdit,
                        onCancel: cancelCellEdit,
                        saving: savingCell,
                        assemblyTypeOptions: assemblyType,
                      });
                      return (
                        <tr
                          key={index}
                          className="bg-white border-b hover:bg-slate-200 text-sm md:text-base cursor-pointer text-gray-900"
                          onClick={() => assembly_detail(data?._id)}
                        >
                          <EditableCell className='font-medium whitespace-nowrap' {...cellProps('assembly_number')} />
                          <EditableCell {...cellProps('assembly_type')} />
                          <EditableCell className='min-w-64' {...cellProps('assembly_description')} />
                          <td className='px-4 py-2 ml:py-6' onClick={(e) => e.stopPropagation()}>
                            <div className='flex justify-center items-center gap-2'>
                              <Link
                                to={`/assembly_detail/${data?._id}`}
                                title='View assembly'
                                aria-label='View assembly'
                                className='inline-flex p-1.5 rounded-md bg-white border border-gray-200 hover:bg-orange-400 hover:border-orange-400 text-gray-500 hover:text-white transition-colors'
                              >
                                <EyeIcon size='w-4 h-4' />
                              </Link>
                              <Link
                                to={`/assembly/edit/${data?._id}`}
                                title='Edit assembly'
                                aria-label='Edit assembly'
                                className='inline-flex p-1.5 rounded-md bg-white border border-gray-200 hover:bg-orange-400 hover:border-orange-400 text-gray-500 hover:text-white transition-colors'
                              >
                                <EditIcon size='w-4 h-4' />
                              </Link>
                              <button
                                type='button'
                                title='Delete assembly'
                                aria-label='Delete assembly'
                                onClick={() => openDelete(data)}
                                className='inline-flex p-1.5 rounded-md bg-white border border-gray-200 hover:bg-red-500 hover:border-red-500 text-gray-500 hover:text-white transition-colors'
                              >
                                <DeleteIcon size='w-4 h-4' />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>}
              </table>
              <PaginationComponent
                totalData={totalAssemblies}
                totalPages={totalPages}
                currentPage={params.page}
                limitValue={params.limit}
                setLimitValue={handleLimitValue}
                setCurrentPage={handleCurrentPage}
                siblings={2}
              />
            </div>
          }
        </div>
      </div>
    </>
  )
}

export default ProductLibraryListView